// 순공부시간 현황 페이지

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

// 자녀별 순공부시간 요약 타입
interface ChildStudyTimeSummary {
  student_id: string;
  student_name: string;
  today_minutes: number;
  week_minutes: number;
  month_minutes: number;
  status: string;
  last_entry_time: string | null;
  error?: string;
}

// 일별 순공부시간
interface DailyStudyTime {
  date: string;
  total_minutes: number;
  session_count: number;
}

// 히스토리 응답
interface StudyTimeHistory {
  student_id: string;
  student_name: string;
  start_date: string;
  end_date: string;
  history: DailyStudyTime[];
  total_days: number;
  study_days: number;
  total_minutes: number;
  average_minutes: number;
}

export default function StudyTimePage() {
  const { parent } = useSupabaseParentAuth();

  const [summaries, setSummaries] = useState<ChildStudyTimeSummary[]>([]);
  const [histories, setHistories] = useState<Record<string, StudyTimeHistory>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedChild, setExpandedChild] = useState<string | false>(false);

  useEffect(() => {
    if (parent?.id) {
      loadStudyTimeSummaries();
    }
  }, [parent?.id]);

  const loadStudyTimeSummaries = async () => {
    if (!parent?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch('/api/v1/parent/children/study-time/summary', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('순공부시간 정보 조회 실패');
      }

      const data = await response.json();
      setSummaries(data.children || []);

      // 첫 번째 자녀가 있으면 자동 확장
      if (data.children && data.children.length > 0) {
        setExpandedChild(data.children[0].student_id);
        loadChildStudyTimeHistory(data.children[0].student_id, accessToken);
      }
    } catch (err: any) {
      console.error('순공부시간 조회 오류:', err);
      setError(err.message || '순공부시간 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildStudyTimeHistory = async (studentId: string, accessTokenParam?: string) => {
    const accessToken = accessTokenParam || await getAccessToken();

    if (!accessToken) return;

    try {
      const response = await fetch(`/api/v1/parent/children/${studentId}/study-time/history`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('순공부시간 히스토리 조회 실패');
      }

      const data = await response.json();
      setHistories(prev => ({
        ...prev,
        [studentId]: data,
      }));
    } catch (err) {
      console.error('순공부시간 히스토리 조회 오류:', err);
    }
  };

  const handleAccordionChange = (studentId: string) => async (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedChild(isExpanded ? studentId : false);

    if (isExpanded && !histories[studentId]) {
      loadChildStudyTimeHistory(studentId);
    }
  };

  const formatMinutes = (minutes: number) => {
    if (minutes === 0) return '0분';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}시간 ${mins}분`;
    } else if (hours > 0) {
      return `${hours}시간`;
    } else {
      return `${mins}분`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });
  };

  const getStatusChip = (status: string) => {
    if (status === 'in_study') {
      return <Chip label="공부중" color="success" size="small" />;
    } else if (status === 'out_study') {
      return <Chip label="퇴실" color="default" size="small" />;
    } else if (status === 'error') {
      return <Chip label="오류" color="error" size="small" />;
    }
    return <Chip label="알 수 없음" color="default" size="small" />;
  };

  const getWeeklyGoalProgress = (weekMinutes: number) => {
    // 주간 목표: 35시간 (2100분)
    const goalMinutes = 2100;
    const progress = Math.min((weekMinutes / goalMinutes) * 100, 100);
    return progress;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        순공부시간
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        자녀의 순공부시간 현황을 확인할 수 있습니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {summaries.length === 0 ? (
        <Alert severity="info">
          등록된 자녀가 없습니다.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {summaries.map((summary) => (
            <Accordion
              key={summary.student_id}
              expanded={expandedChild === summary.student_id}
              onChange={handleAccordionChange(summary.student_id)}
              elevation={2}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                    {summary.student_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {getStatusChip(summary.status)}
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`오늘 ${formatMinutes(summary.today_minutes)}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />

                {/* 시간 요약 카드 */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        오늘
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {formatMinutes(summary.today_minutes)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        이번 주
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="secondary">
                        {formatMinutes(summary.week_minutes)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card sx={{ flex: '1 1 150px', minWidth: 150 }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        이번 달
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {formatMinutes(summary.month_minutes)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* 주간 목표 진행률 */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      주간 목표 (35시간)
                    </Typography>
                    <Typography variant="subtitle2" color="primary">
                      {Math.round(getWeeklyGoalProgress(summary.week_minutes))}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getWeeklyGoalProgress(summary.week_minutes)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>

                {/* 일별 히스토리 */}
                {histories[summary.student_id] ? (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      최근 30일 기록
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {histories[summary.student_id].history.slice(-14).map((day) => (
                        <Box
                          key={day.date}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: day.total_minutes > 0
                              ? day.total_minutes >= 300
                                ? 'success.light'
                                : day.total_minutes >= 120
                                  ? 'warning.light'
                                  : 'grey.200'
                              : 'grey.100',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                          title={`${formatDate(day.date)}: ${formatMinutes(day.total_minutes)}`}
                        >
                          <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
                            {new Date(day.date).getDate()}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 8, color: 'text.secondary' }}>
                            {day.total_minutes > 0 ? Math.floor(day.total_minutes / 60) + 'h' : '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        총 공부일: {histories[summary.student_id].study_days}일
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        평균: {formatMinutes(histories[summary.student_id].average_minutes)}/일
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
